import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  userPreferenceUnitState,
  userPreferencesState,
} from '../../atoms/userPreferencesAtom/userPreferencesAtom';

import { ReactComponent as ChartIcon } from '../../assets/chart.svg';
import ConsumptionChart from './widgets/ConsumptionChart';
import { ConsumptionUnitEnum } from '../../enum/ConsumptionUnitEnum';
import { EanURL } from '../../common/eanUtils';
import EnergyGuidesList from './widgets/energyGuides/EnergyGuidesList';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorState from '../ErrorState';
import { ReactComponent as GaugeIcon } from '../../assets/gauge.svg';
import { ReactComponent as IndexIcon } from '../../assets/index.svg';
import { ReactComponent as InformationIcon } from '../../assets/information.svg';
import { ReactComponent as LockIcon } from '../../assets/open-lock.svg';
import MeterDelegates from './widgets/MeterDelegates';
import MeterForecast from './widgets/forecast/MeterForecast';
import MeterIndex from './widgets/index/MeterIndex';
import MeterSettings from './widgets/MeterSettings';
import { OVERVIEW_TABS } from '../../enum/OverviewTabsEnums';
import { PeriodEnum } from '../../enum/PeriodEnum';
import { RoleAccessResponse } from '../../models/RoleAccessResponse';
import SdpHeader from '../common/SdpHeader';
import { SdpResponse } from '../../models/SdpResponse';
import Skeleton from 'react-loading-skeleton';
import { VolumeTypeEnum } from '../../enum/VolumeTypeEnum';
import { roleAccessState } from '../../atoms/roleAccessAtom';
import { routes } from '../../routes';
import { sdpState } from '../../atoms/sdpAtom';
import { useTranslation } from 'react-i18next';

export const overviewTabs = (roleAccess: RoleAccessResponse) => [
  {
    id: OVERVIEW_TABS.CONSUMPTION,
    label: 'overview.meter.detail.consumption',
    icon: <ChartIcon />,
    isAvailable: roleAccess.myConsumption.isAvailable,
    dataTestId: 'overview-meter-details-tab-consumption',
  },
  {
    id: OVERVIEW_TABS.INDEX,
    label: 'overview.meter.detail.index',
    icon: <IndexIcon />,
    isAvailable: roleAccess.index.isAvailable,
    dataTestId: 'overview-meter-details-tab-index',
  },
  {
    id: OVERVIEW_TABS.FORECAST,
    label: 'overview.meter.detail.forecast',
    icon: <GaugeIcon />,
    isAvailable: roleAccess.forecast.isAvailable,
    dataTestId: 'overview-meter-details-tab-forecast',
  },
  {
    id: OVERVIEW_TABS.SETTINGS,
    label: 'overview.meter.detail.information',
    icon: <InformationIcon />,
    isAvailable: roleAccess.settings.isAvailable,
    dataTestId: 'overview-meter-details-tab-information',
  },
  {
    id: OVERVIEW_TABS.DELEGATES,
    label: 'overview.meter.detail.delegates',
    icon: <LockIcon />,
    isAvailable: roleAccess.delegate.isAvailable,
    dataTestId: 'overview-meter-details-tab-delegates',
  },
];
export default function OverviewMeterDetails() {
  const { t } = useTranslation();
  const { id: sdpId, tabId: selectedTabId } = useParams();
  const navigate = useNavigate();
  const sdpAtom = useRecoilValue(sdpState);
  const [sdp, setSdp] = useState<SdpResponse | null | undefined>(null);
  const [userPreferences, setUserPreferencesState] = useRecoilState(userPreferencesState);
  const roleAccess = useRecoilValue(roleAccessState);
  const selectedTabIndex = useMemo(() => {
    return roleAccess
      ? overviewTabs(roleAccess)
          .filter((tab) => tab.isAvailable)
          .findIndex((tab) => tab.id === userPreferences.selectedOverviewMeterTab)
      : -1;
  }, [roleAccess, userPreferences.selectedOverviewMeterTab]);
  useEffect(() => {
    if (roleAccess) {
      navigate(
        `${routes.withArgs(
          `${routes.OVERVIEW.path}${routes.OVERVIEW.children.OVERVIEW_DETAIL.path}`,
          {
            ':id': EanURL.encrypt(userPreferences.selectedSdpId!) as string,
            ':tabId': overviewTabs(roleAccess).filter((tab) => tab.isAvailable)[
              selectedTabIndex >= 0 ? selectedTabIndex : 0
            ].id,
          }
        )}${window.location.search}`
      );
    }
  }, [navigate, userPreferences.selectedSdpId, selectedTabIndex, roleAccess]);
  const handleSelectTab = (index: number) => {
    if (roleAccess) {
      setUserPreferencesState((state) => ({
        ...state,
        selectedOverviewMeterTab: overviewTabs(roleAccess).filter((tab) => tab.isAvailable)[index]
          .id,
      }));
    }
  };
  const userPreferenceUnit = useRecoilValue<ConsumptionUnitEnum>(userPreferenceUnitState);
  const isConsumption = (volumeType?: VolumeTypeEnum) =>
    volumeType?.includes(VolumeTypeEnum.CONSUMPTION) &&
    !volumeType?.includes(VolumeTypeEnum.CONSUMPTION_SHARED) &&
    !volumeType?.includes(VolumeTypeEnum.CONSUMPTION_NETWORK);

  useEffect(() => {
    if (sdpId) {
      setSdp(sdpAtom.items?.find((item) => item.id === userPreferences.selectedSdpId));
      if (roleAccess) {
        setUserPreferencesState((state) => ({
          ...state,
          selectedSdpId: EanURL.decrypt(sdpId) as string,
          selectedOverviewMeterTab: overviewTabs(roleAccess).filter(
            (tab) => tab.isAvailable && tab.id === selectedTabId
          )[0]?.id,
        }));
      }
    }
  }, [
    sdpId,
    sdpAtom.items,
    userPreferences.selectedSdpId,
    setUserPreferencesState,
    roleAccess,
    selectedTabId,
  ]);

  const tabItemRender = ({
    label,
    icon,
    dataTestId,
  }: {
    label: string;
    icon: React.ReactNode;
    dataTestId: string;
  }) => {
    return (
      <Tab
        className="react-tabs__tab overview-meter-details__tabs__item"
        data-test-id={dataTestId}
        key={label}
      >
        <div className="overview-meter-details__tabs__item__content--large">{label}</div>
        <div className="overview-meter-details__tabs__item__content">{icon}</div>
      </Tab>
    );
  };

  const dateComparator = (a: string, b: string) => {
    if (userPreferences.periodGranularity === PeriodEnum.YEARLY) {
      return new Date(a).getFullYear() === new Date(b).getFullYear();
    } else {
      return (
        new Date(a).getMonth() === new Date(b).getMonth() &&
        new Date(a).getFullYear() === new Date(b).getFullYear()
      );
    }
  };

  return (
    <article>
      <SdpHeader sdp={sdp} />
      <div className="overview-meter-details__tabs">
        <Tabs
          selectedIndex={selectedTabIndex >= 0 ? selectedTabIndex : 0}
          onSelect={handleSelectTab}
        >
          <TabList>
            {roleAccess ? (
              overviewTabs(roleAccess)
                .filter((tab) => tab.isAvailable)
                .map((tab) =>
                  tabItemRender({
                    label: t(tab.label),
                    icon: tab.icon,
                    dataTestId: tab.dataTestId,
                  })
                )
            ) : (
              <Skeleton count={1} height={400} width="100%" />
            )}
          </TabList>
          {roleAccess?.myConsumption.isAvailable && (
            <TabPanel>
              <ErrorBoundary FallbackComponent={() => <ErrorState />}>
                <ConsumptionChart
                  sdp={sdp}
                  volumesType={[
                    VolumeTypeEnum.INJECTION_DAY,
                    VolumeTypeEnum.INJECTION_NIGHT,
                    VolumeTypeEnum.CONSUMPTION_DAY,
                    VolumeTypeEnum.CONSUMPTION_NIGHT,
                  ].filter((item) => {
                    if (userPreferenceUnit === ConsumptionUnitEnum.EURO) {
                      return item.includes(VolumeTypeEnum.CONSUMPTION);
                    }
                    return item;
                  })}
                  dataset={(
                    consumptions,
                    previousCompareConsumptions,
                    energySharingInjections,
                    energySharingOffTake,
                    categories
                  ) => [
                    {
                      name: 'common.sampling.previous.period',
                      color: '#a3117b',
                      opacity: 0.5,
                      stack: 'prevSumConsumption',
                      data:
                        categories && previousCompareConsumptions
                          ? categories?.map(
                              (item) =>
                                previousCompareConsumptions
                                  .filter((data) => {
                                    if (userPreferences.periodGranularity === PeriodEnum.MONTHLY) {
                                      return (
                                        new Date(data.time).getMonth() === new Date(item).getMonth()
                                      );
                                    }
                                    return false;
                                  })
                                  .find((item) => isConsumption(item.volumeType))?.total ?? 0
                            )
                          : ([] as any),
                    },
                    {
                      name: 'common.sampling',
                      color: '#a3117b',
                      stack: 'sumConsumption',
                      data:
                        categories && consumptions
                          ? categories?.map(
                              (item) =>
                                consumptions
                                  .filter((data) => data.time === item)
                                  .find((item) => isConsumption(item.volumeType))?.total
                            )
                          : ([] as any),
                    },
                    {
                      name: 'common.injection.previous.period',
                      color: '#ce7eb8',
                      stack: 'prevSumConsumption',
                      opacity: 0.5,
                      data:
                        categories && previousCompareConsumptions
                          ? categories?.map(
                              (item) =>
                                previousCompareConsumptions
                                  .filter((data) => {
                                    if (userPreferences.periodGranularity === PeriodEnum.MONTHLY) {
                                      return (
                                        new Date(data.time).getMonth() === new Date(item).getMonth()
                                      );
                                    }
                                    return false;
                                  })
                                  .find((item) =>
                                    item.volumeType?.includes(VolumeTypeEnum.INJECTION)
                                  )?.total ?? 0
                            )
                          : ([] as any),
                    },
                    {
                      name: 'common.injection',
                      color: '#ce7eb8',
                      stack: 'sumConsumption',
                      data:
                        categories && consumptions
                          ? categories?.map(
                              (item) =>
                                consumptions!
                                  .filter((data) => data.time === item)
                                  .find((item) =>
                                    item.volumeType?.includes(VolumeTypeEnum.INJECTION)
                                  )?.total
                            )
                          : ([] as any),
                    },
                    {
                      name: 'common.full.hour',
                      color: '#e88300',
                      stack: 'consumption',
                      data:
                        categories && consumptions
                          ? categories?.map(
                              (item) =>
                                consumptions!
                                  .filter((data) => data.time === item)
                                  .find((item) => isConsumption(item.volumeType))?.day
                            )
                          : ([] as any),
                    },
                    {
                      name: 'common.mid.hour',
                      color: '#ffe5b4',
                      stack: 'consumption',
                      data: categories?.map(
                        (item) =>
                          consumptions
                            ?.filter((data) => data.time === item)
                            .find((item) => isConsumption(item.volumeType))?.night
                      ),
                    },
                    {
                      name: 'common.shared',
                      color: '#4b84e4',
                      stack: 'energySharing',
                      data: categories?.map(
                        (item) =>
                          energySharingOffTake?.find((data) =>
                            dateComparator(item, data.meterLastUpdate)
                          )?.complementaryVolume ?? 0
                      ),
                    },
                    {
                      name: 'common.network',
                      color: '#b3d7f9',
                      stack: 'energySharing',
                      data: categories?.map(
                        (item) =>
                          energySharingOffTake?.find((data) =>
                            dateComparator(item, data.meterLastUpdate)
                          )?.localVolume ?? 0
                      ),
                    },
                    {
                      name: 'common.injection.shared',
                      color: '#f54aff',
                      stack: 'energySharing',
                      data: categories?.map(
                        (item) =>
                          energySharingInjections?.find((data) =>
                            dateComparator(item, data.meterLastUpdate)
                          )?.complementaryVolume ?? 0
                      ),
                    },
                    {
                      name: 'common.injection.network',
                      color: '#fddeff',
                      stack: 'energySharing',
                      data: categories?.map(
                        (item) =>
                          energySharingInjections?.find((data) =>
                            dateComparator(item, data.meterLastUpdate)
                          )?.localVolume ?? 0
                      ),
                    },
                  ]}
                />
              </ErrorBoundary>
              <EnergyGuidesList />
            </TabPanel>
          )}
          {roleAccess?.index.isAvailable && (
            <TabPanel>
              <ErrorBoundary FallbackComponent={() => <ErrorState />}>
                <MeterIndex sdp={sdp} />
                <EnergyGuidesList />
              </ErrorBoundary>
            </TabPanel>
          )}
          {roleAccess?.forecast.isAvailable && (
            <TabPanel>
              <ErrorBoundary FallbackComponent={() => <ErrorState />}>
                <MeterForecast sdp={sdp} />
                <EnergyGuidesList />
              </ErrorBoundary>
            </TabPanel>
          )}
          {roleAccess?.settings.isAvailable && (
            <TabPanel>
              <MeterSettings sdp={sdp} />
              <EnergyGuidesList />
            </TabPanel>
          )}
          {roleAccess?.delegate.isAvailable && (
            <TabPanel>
              <ErrorBoundary FallbackComponent={() => <ErrorState />}>
                <MeterDelegates sdp={sdp} />
                <EnergyGuidesList />
              </ErrorBoundary>
            </TabPanel>
          )}
        </Tabs>
      </div>
    </article>
  );
}
